import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { adminBrandQuery } from '../../graphql/queries';
import { useState } from 'react';
// @ts-ignore
import nestedProperty from 'nested-property';
import i18n from '@/utils/i18n';
import { updateBrandInvoiceInfoMutation } from '../../graphql/mutations';
// @ts-ignore
import HelpPopover from '../../components/HelpPopover';
import { formatPercent, centsToPrice } from '@/utils/formatter';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import { DEFAULT_CURRENCY } from '@/utils/price';
import { isValidEmailInput } from '@/utils/email';
import { Button } from 'reactstrap';
import { ContentSection } from '../../components/Layout/ContentSection';
import { DunningReminderBlockType } from '../../graphql/generatedTypes';

type MerchantEditBillingInfoTabProps = {
  externalPickpackId: string | null;
};

export const MerchantEditBillingInfoTab: React.FC<
  MerchantEditBillingInfoTabProps
> = ({ externalPickpackId }) => {
  const [brandUpdate, setBrandUpdate] = useState<any | undefined>(null);
  const [updateBrandInvoice, { loading: updating }] = useMutation(
    updateBrandInvoiceInfoMutation,
  );
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const { data = { brand: null }, loading: loadingBrand } = useQuery(
    adminBrandQuery,
    {
      fetchPolicy: 'no-cache',
      variables: { id: externalPickpackId },
      onCompleted: (brandData) => {
        // @ts-ignore
        setBrandUpdate(brandData.brand);
        // @ts-ignore
        const currencies: string[] = (brandData.stores || [])
          .map((s: any) => s.currency)
          .sort();
        // all are the same
        if (
          currencies.length > 0 &&
          currencies[0] === currencies[currency.length - 1]
        ) {
          setCurrency(currencies[0]);
        }
      },
    },
  );

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;
    if (isNumber) formattedValue = Number(value);
    if (isDate) {
      try {
        formattedValue = new Date(value).toISOString();
      } catch {}
    }

    setBrandUpdate((oldBrandUpdate: any) => {
      let newBrandUpdate = { ...oldBrandUpdate };
      nestedProperty.set(newBrandUpdate, name, formattedValue);
      return newBrandUpdate;
    });
  }

  function handleSave() {
    const mutationInput: any = {
      address2: {
        ...brandUpdate.address2,
        __typename: undefined,
      },
      monthly_fee: Number(brandUpdate.monthly_fee),
      onboarding_fee_vytal: Number(brandUpdate.onboarding_fee_vytal),
      commission_rate: Number(brandUpdate.commission_rate),
      legal_name: brandUpdate.legal_name,
      prefix: brandUpdate.prefix,
      invoice_email: brandUpdate.invoice_email,
      separate_invoice_each_store: Boolean(
        brandUpdate.separate_invoice_each_store,
      ),
      regular_payment_target_days:
        brandUpdate.regular_payment_target_days &&
        brandUpdate.regular_payment_target_days !== ''
          ? Number(brandUpdate.regular_payment_target_days)
          : null,
      dunning_reminder_block_type: brandUpdate.dunning_reminder_block_type
        ? brandUpdate.dunning_reminder_block_type
        : null,
      dunning_reminder_block_reason:
        brandUpdate.dunning_reminder_block_reason?.trim() || null,
    };

    updateBrandInvoice({
      variables: {
        id: brandUpdate.id,
        brand: mutationInput,
      },
      update: (cache, { data: { updateBrandInvoiceInfo: updatedBrand } }) => {
        setBrandUpdate(updatedBrand);
      },
    }).catch((err) => {
      alert(i18n.t('Merchants:errorUpdatingBrand', { error: err }));
    });
  }

  if (!externalPickpackId) {
    return <p>{i18n.t('Merchants:noExternalPickpackId')}</p>;
  }

  // @ts-ignore
  if (
    loadingBrand ||
    !brandUpdate ||
    data.brand === null ||
    data.brand.id !== brandUpdate.id
  ) {
    return <LoadingContainer />;
  }

  return (
    <ContentSection title={i18n.t('Billing:billingInfo')} collapsable={false}>
      <EditSection onSave={handleSave} isLoading={updating}>
        <EditItem title={i18n.t('Billing:billingInfo')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:monthlyFee')} (
              {centsToPrice(brandUpdate.monthly_fee, currency)})
            </span>
            <input
              type="number"
              className={'form-control'}
              name="monthly_fee"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              step="1"
              value={brandUpdate.monthly_fee || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:vytalOnboardingFee')} (
              {centsToPrice(brandUpdate.onboarding_fee_vytal, currency)})
            </span>
            <input
              type="number"
              className={'form-control'}
              name="onboarding_fee_vytal"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              step="1"
              value={brandUpdate.onboarding_fee_vytal || ''}
              placeholder={''}
            />
            <HelpPopover
              text={i18n.t('Billing:onboardingFeeHelpText')}
              id="onboarding_fee_vytal"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:preorderCommissionRate')} (
              {formatPercent(brandUpdate.commission_rate)})
            </span>
            <input
              type="number"
              className={'form-control'}
              name="commission_rate"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              step="1"
              value={brandUpdate.commission_rate || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:prefix')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="prefix"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={brandUpdate.prefix || ''}
              placeholder={'XX'}
            />
            <HelpPopover text={i18n.t('Billing:prefixHelpText')} id="prefix" />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:invoiceEmail')}
            </span>
            <input
              type="text"
              className={
                'form-control ' +
                (!isValidEmailInput(brandUpdate.invoice_email)
                  ? 'is-invalid'
                  : '')
              }
              name="invoice_email"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={brandUpdate.invoice_email || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:legalName')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="legal_name"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={brandUpdate.legal_name || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:street')}
            </span>
            <input
              type="text"
              className="form-control"
              name="address2.streetname"
              onChange={handleChange}
              value={
                (brandUpdate.address2 && brandUpdate.address2.streetname) || ''
              }
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:streetNo')}
            </span>
            <input
              type="text"
              className="form-control"
              onChange={handleChange}
              name="address2.streetno"
              value={
                (brandUpdate.address2 && brandUpdate.address2.streetno) || ''
              }
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Billing:zipCode')}
            </span>
            <input
              type="text"
              className="form-control"
              name="address2.zip"
              onChange={handleChange}
              value={(brandUpdate.address2 && brandUpdate.address2.zip) || ''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:city')}
            </span>
            <input
              type="text"
              className="form-control"
              name="address2.city"
              onChange={handleChange}
              value={(brandUpdate.address2 && brandUpdate.address2.city) || ''}
              placeholder={i18n.t('Billing:cityPlaceholder')}
            />
          </div>
        </EditItem>
        <Button onClick={handleSave} className="float-end">
          {i18n.t('Billing:saveBrandInvoiceInfo')}
        </Button>
      </EditSection>
    </ContentSection>
  );
};
